"use client";

import { type RefObject, useEffect } from "react";
import { loadGsapWithScrollTrigger, shouldReduceMotion } from "@/lib/gsap";

type ServiceCardsAnimationPreset = "cascade" | "tilt" | "drift";

type UseServiceCardsAnimationOptions = {
	preset?: ServiceCardsAnimationPreset;
	start?: string;
	end?: string;
};

const presetConfig: Record<
	ServiceCardsAnimationPreset,
	{
		duration: number;
		ease: string;
		staggerEach: number;
		from: "start" | "center";
		getCardFrom: (index: number) => Record<string, number | string>;
		getMediaFrom?: (index: number) => Record<string, number | string>;
	}
> = {
	cascade: {
		duration: 1.2,
		ease: "expo.out",
		staggerEach: 0.1,
		from: "start",
		getCardFrom: (index) => ({
			autoAlpha: 0,
			y: 48,
			x: index % 2 === 0 ? -18 : 18,
			rotateZ: index % 2 === 0 ? -2.5 : 2.5,
			scale: 0.965,
			filter: "blur(10px)",
		}),
		getMediaFrom: (index) => ({
			autoAlpha: 0,
			y: index % 2 === 0 ? 18 : -18,
			scale: 0.9,
			rotateZ: index % 2 === 0 ? -4 : 4,
		}),
	},
	tilt: {
		duration: 1.3,
		ease: "power4.out",
		staggerEach: 0.12,
		from: "start",
		getCardFrom: (index) => ({
			autoAlpha: 0,
			yPercent: 14,
			rotationX: index % 2 === 0 ? -14 : 14,
			rotateZ: index % 2 === 0 ? -1.5 : 1.5,
			transformPerspective: 1000,
			transformOrigin: "50% 100%",
			filter: "blur(8px)",
		}),
		getMediaFrom: () => ({
			autoAlpha: 0,
			y: 20,
			scale: 0.88,
		}),
	},
	drift: {
		duration: 1.15,
		ease: "power3.out",
		staggerEach: 0.08,
		from: "center",
		getCardFrom: (index) => ({
			autoAlpha: 0,
			y: 34,
			x: index % 3 === 0 ? -26 : index % 3 === 1 ? 0 : 26,
			scale: 0.94,
			filter: "blur(12px)",
		}),
		getMediaFrom: (index) => ({
			autoAlpha: 0,
			y: index % 2 === 0 ? 14 : -14,
			scale: 0.92,
		}),
	},
};

export function useServiceCardsAnimation(
	sectionRef: RefObject<HTMLElement | null>,
	{
		preset = "cascade",
		start = "top 135%",
	}: UseServiceCardsAnimationOptions = {},
) {
	useEffect(() => {
		if (shouldReduceMotion()) {
			return;
		}

		let cleanup: (() => void) | undefined;
		let cancelled = false;

		async function setupAnimation() {
			const { gsap } = await loadGsapWithScrollTrigger();

			if (cancelled) {
				return;
			}

			const ctx = gsap.context(() => {
				const cards = gsap.utils
					.toArray<HTMLElement>("[data-service-card]")
					.filter((card) => card.offsetParent !== null);
				const trigger =
					gsap.utils
						.toArray<HTMLElement>("[data-service-cards-animation-trigger]")
						.find((item) => item.offsetParent !== null) ?? sectionRef.current;

				if (!cards.length || !trigger) {
					return;
				}

				const media = cards
					.map((card) =>
						card.querySelector<HTMLElement>("[data-service-card-media]"),
					)
					.filter(Boolean);
				const config = presetConfig[preset];

				cards.forEach((card, index) => {
					gsap.set(card, {
						willChange: "transform, opacity, filter",
						...config.getCardFrom(index),
					});
				});

				media.forEach((item, index) => {
					if (!config.getMediaFrom) {
						return;
					}

					gsap.set(item, {
						willChange: "transform, opacity",
						...config.getMediaFrom(index),
					});
				});

				const timeline = gsap.timeline({
					scrollTrigger: {
						trigger,
						start,
						toggleActions: "play reverse restart reverse",
					},
				});

				timeline.to(cards, {
					autoAlpha: 1,
					y: 0,
					x: 0,
					yPercent: 0,
					rotationX: 0,
					rotateZ: 0,
					scale: 1,
					filter: "blur(0px)",
					duration: config.duration,
					ease: config.ease,
					clearProps: "willChange",
					stagger: {
						each: config.staggerEach,
						from: config.from,
					},
				});

				if (media.length) {
					timeline.to(
						media,
						{
							autoAlpha: 1,
							y: 0,
							scale: 1,
							rotateZ: 0,
							duration: 0.95,
							ease: "power3.out",
							clearProps: "willChange",
							stagger: 0.06,
						},
						"<0.18",
					);
				}
			}, sectionRef);

			cleanup = () => ctx.revert();
		}

		void setupAnimation();

		return () => {
			cancelled = true;
			cleanup?.();
		};
	}, [preset, sectionRef, start]);
}
